import React, { useState, useMemo } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../ui/dialog";
import { toast } from "sonner";
import {
    Search,
    Filter,
    Plus,
    BookOpen,
    DollarSign,
    Award,
    Wallet,
    MoreVertical,
    Edit2,
    Trash2,
    Eye,
    Inbox,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "../ui/dropdown-menu";

interface FeeStructure {
    id: string;
    academicYear: string;
    course: string;
    specialization: string;
    feeType: string;
    duration: string;
    baseAmount: number;
    scholarshipApplied: boolean;
    netPayable: number;
    status: "Active" | "Draft";
    createdAt: string;
}

export function FeesStructure() {
    const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([
        {
            id: "FS-001",
            academicYear: "2024-2025",
            course: "B.Tech",
            specialization: "Computer Science",
            feeType: "Tuition",
            duration: "Semester",
            baseAmount: 120000,
            scholarshipApplied: true,
            netPayable: 108000,
            status: "Active",
            createdAt: "2024-01-15",
        },
        {
            id: "FS-002",
            academicYear: "2024-2025",
            course: "MBA",
            specialization: "Finance",
            feeType: "Admission",
            duration: "One-Time",
            baseAmount: 50000,
            scholarshipApplied: false,
            netPayable: 50000,
            status: "Active",
            createdAt: "2024-01-20",
        },
    ]);

    const [searchQuery, setSearchQuery] = useState("");
    const [filterYear, setFilterYear] = useState("All");
    const [filterCourse, setFilterCourse] = useState("All");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
    const [editingId, setEditingId] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        academicYear: "",
        course: "",
        specialization: "",
        feeType: "",
        duration: "",
        baseFee: "",
        additionalCharges: "",
        tax: "",
        scholarshipType: "",
        scholarshipMode: "Percentage",
        scholarshipValue: "",
    });

    const resetForm = () => {
        setFormData({
            academicYear: "",
            course: "",
            specialization: "",
            feeType: "",
            duration: "",
            baseFee: "",
            additionalCharges: "",
            tax: "",
            scholarshipType: "",
            scholarshipMode: "Percentage",
            scholarshipValue: "",
        });
        setEditingId(null);
    }

    const openCreateModal = () => {
        resetForm();
        setModalMode("create");
        setIsModalOpen(true);
    };

    const openEditModal = (id: string) => {
        const item = feeStructures.find(f => f.id === id);
        if (item) {
            setFormData({
                academicYear: item.academicYear,
                course: item.course,
                specialization: item.specialization,
                feeType: item.feeType,
                duration: item.duration,
                baseFee: item.baseAmount.toString(),
                additionalCharges: "",
                tax: "",
                scholarshipType: item.scholarshipApplied ? "Need-Based" : "None",
                scholarshipMode: "Fixed",
                scholarshipValue: item.scholarshipApplied ? (item.baseAmount - item.netPayable).toString() : "",
            });
            setEditingId(id);
            setModalMode("edit");
            setIsModalOpen(true);
        }
    };

    const openViewModal = (id: string) => {
        const item = feeStructures.find(f => f.id === id);
        if (item) {
            setFormData({
                academicYear: item.academicYear,
                course: item.course,
                specialization: item.specialization,
                feeType: item.feeType,
                duration: item.duration,
                baseFee: item.baseAmount.toString(),
                additionalCharges: "",
                tax: "",
                scholarshipType: item.scholarshipApplied ? "Need-Based" : "None",
                scholarshipMode: "Fixed",
                scholarshipValue: item.scholarshipApplied ? (item.baseAmount - item.netPayable).toString() : "",
            });
            setEditingId(id);
            setModalMode("view");
            setIsModalOpen(true);
        }
    };

    const baseFee = parseFloat(formData.baseFee) || 0;
    const additional = parseFloat(formData.additionalCharges) || 0;
    const tax = parseFloat(formData.tax) || 0;
    const totalAmount = baseFee + additional + tax;

    const scholarshipVal = parseFloat(formData.scholarshipValue) || 0;
    const scholarshipAmount =
        formData.scholarshipMode === "Percentage"
            ? (totalAmount * scholarshipVal) / 100
            : scholarshipVal;

    const netPayable = Math.max(0, totalAmount - scholarshipAmount);

    const isFormValid =
        formData.academicYear &&
        formData.course &&
        formData.feeType &&
        formData.duration &&
        formData.baseFee;

    const handleSave = () => {
        if (!isFormValid) return;

        if (modalMode === "edit" && editingId) {
            setFeeStructures(feeStructures.map(f => {
                if (f.id === editingId) {
                    return {
                        ...f,
                        academicYear: formData.academicYear,
                        course: formData.course,
                        specialization: formData.specialization || "General",
                        feeType: formData.feeType,
                        duration: formData.duration,
                        baseAmount: baseFee,
                        scholarshipApplied: scholarshipAmount > 0,
                        netPayable: netPayable,
                    };
                }
                return f;
            }));
            toast.success("Fee Structure Updated");
        } else {
            const newEntry: FeeStructure = {
                id: `FS-00${feeStructures.length + 1}`,
                academicYear: formData.academicYear,
                course: formData.course,
                specialization: formData.specialization || "General",
                feeType: formData.feeType,
                duration: formData.duration,
                baseAmount: baseFee,
                scholarshipApplied: scholarshipAmount > 0,
                netPayable: netPayable,
                status: "Active",
                createdAt: new Date().toISOString().split("T")[0],
            };

            setFeeStructures([newEntry, ...feeStructures]);
            toast.success("Fee Structure Created");
        }

        setIsModalOpen(false);
        resetForm();
    };

    const handleDelete = (id: string) => {
        if (window.confirm("Are you sure you want to delete this fee structure?")) {
            setFeeStructures((prev) => prev.filter((fs) => fs.id !== id));
            toast.success("Fee Structure Deleted");
        }
    };

    const filteredData = useMemo(() => {
        return feeStructures.filter((item) => {
            const matchesSearch =
                item.course.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.specialization.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.feeType.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesYear = filterYear === "All" || item.academicYear === filterYear;
            const matchesCourse = filterCourse === "All" || item.course === filterCourse;

            return matchesSearch && matchesYear && matchesCourse;
        });
    }, [feeStructures, searchQuery, filterYear, filterCourse]);

    return (
        <div className="min-h-screen bg-background p-6 lg:p-8 text-slate-900 dark:text-white">
            <div className="w-full space-y-8">

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b pb-6 dark:border-white/10">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">Fees Structure</h1>
                        <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-2">Manage course and specialization-based fee configurations</p>
                    </div>

                    <Button
                        className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 px-8 transition-all active:scale-95 h-11"
                        onClick={openCreateModal}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        <span className="text-[13px] font-bold uppercase tracking-widest">Create Fee Structure</span>
                    </Button>

                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="hover:-translate-y-1 transition-transform duration-300 border border-white/10 shadow-sm bg-card">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Total Fee Structures</p>
                                    <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">{feeStructures.length}</p>
                                </div>

                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
                                    <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover:-translate-y-1 transition-transform duration-300 border border-white/10 shadow-sm bg-card">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Active Courses</p>
                                    <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">12</p>
                                </div>

                                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                                    <Award className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover:-translate-y-1 transition-transform duration-300 border border-white/10 shadow-sm bg-card">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Scholarship Configs</p>
                                    <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">5</p>
                                </div>

                                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
                                    <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover:-translate-y-1 transition-transform duration-300 border border-white/10 shadow-sm bg-card">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Annual Revenue</p>
                                    <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">₹2.4Cr</p>
                                </div>

                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                                    <Wallet className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Card className="border border-white/10 shadow-sm bg-card rounded-2xl overflow-hidden">
                    <div className="p-6 border-b dark:border-white/5 flex flex-col md:flex-row items-center gap-4 justify-between">
                        <div className="flex items-center gap-2 flex-1 w-full md:w-auto">
                            <div className="relative w-full max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search courses, types..."
                                    className="pl-9 bg-background border-white/5 shadow-none rounded-xl text-sm font-medium"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />

                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <Select value={filterYear} onValueChange={setFilterYear}>
                                <SelectTrigger className="w-[160px] rounded-xl bg-background border border-white/5">
                                    <div className="flex items-center gap-2">
                                        <Filter className="h-4 w-4 text-muted-foreground" />
                                        <SelectValue placeholder="Academic Year" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-lg">
                                    <SelectItem value="All">All Years</SelectItem>
                                    <SelectItem value="2023-2024">2023-2024</SelectItem>
                                    <SelectItem value="2024-2025">2024-2025</SelectItem>
                                </SelectContent>
                            </Select>

                            <Select value={filterCourse} onValueChange={setFilterCourse}>
                                <SelectTrigger className="w-[160px] rounded-xl bg-background border border-white/5">
                                    <SelectValue placeholder="Course" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-lg">
                                    <SelectItem value="All">All Courses</SelectItem>
                                    <SelectItem value="B.Tech">B.Tech</SelectItem>
                                    <SelectItem value="MBA">MBA</SelectItem>
                                    <SelectItem value="BBA">BBA</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {filteredData.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent border-b border-white/5">
                                        <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 pl-6">ID</TableHead>
                                        <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Academic Year</TableHead>
                                        <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Course</TableHead>
                                        <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Fee Type</TableHead>
                                        <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Duration</TableHead>
                                        <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Base Amount</TableHead>
                                        <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Net Payable</TableHead>
                                        <TableHead className="text-center text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Status</TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                </TableHeader>

                                <TableBody>
                                    {filteredData.map((row) => (
                                        <TableRow key={row.id} className="group hover:bg-slate-50/50 dark:hover:bg-white/[0.02] border-b-slate-50 dark:border-white/5 transition-colors">
                                            <TableCell className="font-medium text-muted-foreground">{row.id}</TableCell>
                                            <TableCell>{row.academicYear}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{row.course}</span>
                                                    <span className="text-xs text-muted-foreground">{row.specialization}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border-slate-200 dark:border-white/10 bg-transparent">
                                                    {row.feeType}
                                                </Badge>
                                            </TableCell>

                                            <TableCell>{row.duration}</TableCell>
                                            <TableCell className="text-right font-medium">₹{row.baseAmount.toLocaleString()}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex flex-col items-end">
                                                    <span className="font-bold text-primary">₹{row.netPayable.toLocaleString()}</span>
                                                    {row.scholarshipApplied && (
                                                        <span className="text-[10px] text-emerald-500 font-medium">Scholarship Active</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge
                                                    className={row.status === 'Active'
                                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-widest border-none px-2 py-0.5'
                                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest border-none px-2 py-0.5'
                                                    }
                                                >
                                                    {row.status}
                                                </Badge>
                                            </TableCell>

                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-40 bg-card border border-white/10 shadow-lg">
                                                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="cursor-pointer" onClick={() => openViewModal(row.id)}>
                                                            <Eye className="h-4 w-4 mr-2" /> View Details
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem className="cursor-pointer" onClick={() => openEditModal(row.id)}>
                                                            <Edit2 className="h-4 w-4 mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="cursor-pointer text-red-600 focus:text-red-600" onClick={() => handleDelete(row.id)}>
                                                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                                <div className="h-20 w-20 bg-background border border-white/5 rounded-full flex items-center justify-center mb-6">
                                    <Inbox className="h-10 w-10 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">No fee structures found</h3>
                                <Button onClick={openCreateModal} className="rounded-full">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Create Your First Fee Structure
                                </Button>
                            </div>
                        )}
                    </div>
                </Card>

                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-card rounded-lg shadow-lg border border-white/10">
                        <div className="p-6 sm:p-8">
                            <DialogHeader className="mb-6">
                                <DialogTitle className="text-2xl font-bold">
                                    {modalMode === "create" && "Create Fee Structure"}
                                    {modalMode === "edit" && "Edit Fee Structure"}
                                    {modalMode === "view" && "View Fee Structure"}
                                </DialogTitle>
                            </DialogHeader>

                            <div className="space-y-8 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                <div className="space-y-4">
                                    <h3 className="text-[11px] font-bold tracking-widest text-blue-600 uppercase">1. Academic Details</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Academic Year *</label>
                                            <Select disabled={modalMode === 'view'} value={formData.academicYear} onValueChange={(val) => setFormData({ ...formData, academicYear: val })}>
                                                <SelectTrigger className="rounded-xl bg-white dark:bg-slate-900">
                                                    <SelectValue placeholder="Select Year" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-xl">
                                                    <SelectItem value="2023-2024">2023-2024</SelectItem>
                                                    <SelectItem value="2024-2025">2024-2025</SelectItem>
                                                    <SelectItem value="2025-2026">2025-2026</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Course *</label>
                                            <Select disabled={modalMode === 'view'} value={formData.course} onValueChange={(val) => setFormData({ ...formData, course: val })}>
                                                <SelectTrigger className="rounded-xl bg-white dark:bg-slate-900">
                                                    <SelectValue placeholder="Select Course" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-xl">
                                                    <SelectItem value="B.Tech">B.Tech</SelectItem>
                                                    <SelectItem value="MBA">MBA</SelectItem>
                                                    <SelectItem value="BBA">BBA</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Specialization</label>
                                            <Select disabled={modalMode === 'view' || !formData.course} value={formData.specialization} onValueChange={(val) => setFormData({ ...formData, specialization: val })}>
                                                <SelectTrigger className="rounded-xl bg-white dark:bg-slate-900">
                                                    <SelectValue placeholder="Select Specialization" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-xl">
                                                    <SelectItem value="Computer Science">Computer Science</SelectItem>
                                                    <SelectItem value="Electronics">Electronics</SelectItem>
                                                    <SelectItem value="Finance">Finance</SelectItem>
                                                    <SelectItem value="Marketing">Marketing</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Fee Type *</label>
                                            <Select disabled={modalMode === 'view'} value={formData.feeType} onValueChange={(val) => setFormData({ ...formData, feeType: val })}>
                                                <SelectTrigger className="rounded-xl bg-white dark:bg-slate-900">
                                                    <SelectValue placeholder="Select Type" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-xl">
                                                    <SelectItem value="Tuition">Tuition</SelectItem>
                                                    <SelectItem value="Admission">Admission</SelectItem>
                                                    <SelectItem value="Hostel">Hostel</SelectItem>
                                                    <SelectItem value="Lab">Lab</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2 sm:col-span-2">
                                            <label className="text-sm font-medium">Duration Type *</label>
                                            <Select disabled={modalMode === 'view'} value={formData.duration} onValueChange={(val) => setFormData({ ...formData, duration: val })}>
                                                <SelectTrigger className="rounded-xl bg-white dark:bg-slate-900">
                                                    <SelectValue placeholder="Select Duration" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white dark:bg-slate-950 border dark:border-white/10 shadow-xl">
                                                    <SelectItem value="Semester">Semester</SelectItem>
                                                    <SelectItem value="Annual">Annual</SelectItem>
                                                    <SelectItem value="One-Time">One-Time</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-[11px] font-bold tracking-widest text-blue-600 uppercase">2. Fee Configuration</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Base Fee *</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                                <Input readOnly={modalMode === 'view'} type="number" placeholder="0.00" className="pl-7 rounded-xl bg-background border-white/5" value={formData.baseFee} onChange={(e) => setFormData({ ...formData, baseFee: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Additional Charges</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                                <Input readOnly={modalMode === 'view'} type="number" placeholder="0.00" className="pl-7 rounded-xl" value={formData.additionalCharges} onChange={(e) => setFormData({ ...formData, additionalCharges: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Tax / Processing</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                                <Input readOnly={modalMode === 'view'} type="number" placeholder="0.00" className="pl-7 rounded-xl" value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {modalMode !== "view" && (
                                <DialogFooter className="mt-8 gap-3 sm:gap-0">
                                    <Button variant="ghost" className="rounded-xl text-sm font-bold" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                                    <Button className="rounded-xl px-8 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20" disabled={!isFormValid} onClick={handleSave}>
                                        <span className="text-sm font-bold">{modalMode === "edit" ? "Update Structure" : "Save Structure"}</span>
                                    </Button>

                                </DialogFooter>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}